import { useState, type ReactNode } from 'react';
import { isHttpLink } from './gantt/model.ts';

/** A Persons row id, as GET /api/avatars/{personId} takes it (functions/src/avatars/photo.ts). */
const PERSON_ID = /^P-\d{1,6}$/;

/** A short fingerprint of a link, so the address changes with it (FNV-1a). Not a secret. */
function fingerprint(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193);
  return (h >>> 0).toString(36);
}

/**
 * Where the browser loads a photo. An http(s) link with the person's id: the site's own copy,
 * /api/avatars/{personId} (it redirects to the link while there is none). `v` follows the link, so a
 * changed picture is loaded at once, not from the browser's cache. Else the link as it is (data:, no id).
 */
export function photoSrc(image: string, personId = ''): string {
  // isHttpLink: the same whole-link test as the server's redirect (functions/src/avatars/http.ts).
  if (!isHttpLink(image) || !PERSON_ID.test(personId)) return image;
  return `/api/avatars/${personId}?v=${fingerprint(image)}`;
}

/**
 * A person's photo, or `fallback` (the initials) when there is none or it fails to load. Initials only for
 * the link that failed: a new link is tried again. A hook, not a component: the <img> stays in the caller's tree.
 * `personId` (the Persons row): load the site's copy (photoSrc).
 */
export function usePhoto(image: string, alt: string, fallback: ReactNode, personId?: string): ReactNode {
  const [failed, setFailed] = useState('');
  const src = photoSrc(image, personId);
  return src && src !== failed ? <img src={src} alt={alt} loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(src)} /> : fallback;
}
